/* ============================================
   QRLoginApprove — Approve login from mobile PWA
   Opened when scanning QR code from login screen
   ============================================ */

import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'

const API_URL = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'

type ApproveState = 'confirm' | 'loading' | 'success' | 'error'

export default function QRLoginApprove() {
  const { sessionId } = useParams<{ sessionId: string }>()
  const { user, token, loading, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [state, setState] = useState<ApproveState>('confirm')
  const [error, setError] = useState<string | null>(null)

  // If not authenticated, redirect to login with return URL
  if (!loading && !isAuthenticated) {
    const returnUrl = `/qr-login/approve/${sessionId}`
    window.location.href = `/login?returnUrl=${encodeURIComponent(returnUrl)}`
    return null
  }

  if (loading) {
    return (
      <div className="qr-approve-page" id="qr-approve-page">
        <div className="qr-approve-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
            <span className="login-spinner" />
          </div>
        </div>
      </div>
    )
  }

  const roleLabels: Record<string, string> = {
    admin: 'Administrador',
    cliente: 'Cliente',
    partner: 'Partner',
  }

  async function handleApprove() {
    if (!sessionId || !token) return
    setState('loading')
    setError(null)

    try {
      const res = await fetch(`${API_URL}/api/auth/qr/session/${sessionId}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })

      const data = await res.json()

      if (!res.ok) {
        setError(data.error || 'Error al aprobar sesión')
        setState('error')
        return
      }

      setState('success')
    } catch {
      setError('Error de conexión')
      setState('error')
    }
  }

  function handleCancel() {
    navigate(-1)
  }

  return (
    <div className="qr-approve-page" id="qr-approve-page">
      <div className="qr-approve-card" id="qr-approve-card">
        {state === 'success' ? (
          <div className="qr-approve-success" id="qr-approve-success">
            <div className="qr-approve-success-icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h2>¡Login aprobado!</h2>
            <p>El otro dispositivo ya inició sesión.<br />Puedes cerrar esta ventana.</p>
          </div>
        ) : (
          <>
            {/* Shield icon */}
            <div className="qr-approve-icon">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </div>

            <h1>Aprobar inicio de sesión</h1>
            <p>Un dispositivo quiere acceder como:</p>

            {error && <div className="qr-approve-error" id="qr-approve-error">{error}</div>}

            {/* User info */}
            <div className="qr-approve-user-info" id="qr-approve-user-info">
              <p className="qr-approve-user-email">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2" /><polyline points="22,7 12,13 2,7" />
                </svg>
                {user?.email}
              </p>
              <span className="qr-approve-user-role">
                {roleLabels[user?.role || ''] || user?.role}
              </span>
            </div>

            {/* Actions */}
            <div className="qr-approve-actions">
              <button
                type="button"
                className="qr-approve-btn qr-approve-btn--primary"
                onClick={handleApprove}
                disabled={state === 'loading'}
                id="qr-approve-confirm-btn"
              >
                {state === 'loading' ? (
                  <span className="login-spinner" style={{ width: 18, height: 18 }} />
                ) : (
                  <>✅ Aprobar Login</>
                )}
              </button>
              <button
                type="button"
                className="qr-approve-btn qr-approve-btn--secondary"
                onClick={handleCancel}
                disabled={state === 'loading'}
                id="qr-approve-cancel-btn"
              >
                ❌ Cancelar
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
